import { HUBSPOT_STAGES } from "./hubspot/config";

export const getStageValue = (stageName: string): string => {
  return HUBSPOT_STAGES[stageName] || stageName;
};

export const getStageDisplayName = (stageName: string): string => {
  const displayNames: Record<string, string> = {
    BUY_PROCESS_ORDER: "Buy Process Order",
    SELL_PROCESS_ORDER: "Sell Process Order",
    BUY_ATTEMPTING_TO_CONTACT: "Buy Attempting to Contact",
    SELL_ATTEMPTING_TO_CONTACT: "Sell Attempting to Contact",
    BUY_NEGOTIATION: "Buy Negotiation",
    SELL_NEGOTIATION: "Sell Negotiation",
    BUY_PENDING: "Buy Pending",
    SELL_PENDING: "Sell Pending",
    BUY_WAITING_FOR_BUYER: "Buy Waiting for Buyer",
    SELL_WAITING_FOR_BUYER: "Sell Waiting for Buyer",
    BUY_CLOSED_WON: "Buy Closed Won",
    SELL_CLOSED_WON: "Sell Closed Won",
    BUY_BUSINESS_LOST: "Buy Business Lost",
    SELL_BUSINESS_LOST: "Sell Business Lost",
  };

  return displayNames[stageName] || stageName;
};

export const getOrderByField = (
  typeFilter: string,
  stageFilter: string,
): string => {
  if (stageFilter !== "all") {
    return "stage";
  }

  if (typeFilter !== "all") {
    return "type";
  }

  return "updatedAt";
};
/**
 * Stage definitions and utilities for deal management
 * Centralizes all stage-related constants and helper functions
 */

export interface StageDefinition {
  key: string;
  displayName: string;
  value: string;
  category: "buy" | "sell";
}

/**
 * Comprehensive stage definitions object
 * Contains all available deal stages with their metadata
 */
export const STAGE_DEFINITIONS: Record<string, StageDefinition> = {
  BUY_PROCESS_ORDER: {
    key: "BUY_PROCESS_ORDER",
    displayName: "Buy Process Order",
    value: "259476547",
    category: "buy",
  },
  SELL_PROCESS_ORDER: {
    key: "SELL_PROCESS_ORDER",
    displayName: "Sell Process Order",
    value: "264262987",
    category: "sell",
  },
  BUY_ATTEMPTING_TO_CONTACT: {
    key: "BUY_ATTEMPTING_TO_CONTACT",
    displayName: "Buy Attempting to Contact",
    value: "259476548",
    category: "buy",
  },
  SELL_ATTEMPTING_TO_CONTACT: {
    key: "SELL_ATTEMPTING_TO_CONTACT",
    displayName: "Sell Attempting to Contact",
    value: "264262988",
    category: "sell",
  },
  BUY_NEGOTIATION: {
    key: "BUY_NEGOTIATION",
    displayName: "Buy Negotiation",
    value: "259476549",
    category: "buy",
  },
  SELL_NEGOTIATION: {
    key: "SELL_NEGOTIATION",
    displayName: "Sell Negotiation",
    value: "264262989",
    category: "sell",
  },
  BUY_PENDING: {
    key: "BUY_PENDING",
    displayName: "Buy Pending",
    value: "259476550",
    category: "buy",
  },
  SELL_PENDING: {
    key: "SELL_PENDING",
    displayName: "Sell Pending",
    value: "264262990",
    category: "sell",
  },
  BUY_WAITING_FOR_BUYER: {
    key: "BUY_WAITING_FOR_BUYER",
    displayName: "Buy Waiting for Buyer",
    value: "259476551",
    category: "buy",
  },
  SELL_WAITING_FOR_BUYER: {
    key: "SELL_WAITING_FOR_BUYER",
    displayName: "Sell Waiting for Buyer",
    value: "264262991",
    category: "sell",
  },
  BUY_CLOSED_WON: {
    key: "BUY_CLOSED_WON",
    displayName: "Buy Closed Won",
    value: "259476552",
    category: "buy",
  },
  SELL_CLOSED_WON: {
    key: "SELL_CLOSED_WON",
    displayName: "Sell Closed Won",
    value: "264262992",
    category: "sell",
  },
  BUY_BUSINESS_LOST: {
    key: "BUY_BUSINESS_LOST",
    displayName: "Buy Business Lost",
    value: "259476553",
    category: "buy",
  },
  SELL_BUSINESS_LOST: {
    key: "SELL_BUSINESS_LOST",
    displayName: "Sell Business Lost",
    value: "264262993",
    category: "sell",
  },
};

/**
 * Get all stage definitions as an array
 */
export const getAllStages = (): StageDefinition[] => {
  return Object.values(STAGE_DEFINITIONS);
};

/**
 * Get stages filtered by category
 */
export const getStagesByCategory = (
  category: "buy" | "sell",
): StageDefinition[] => {
  return getAllStages().filter((stage) => stage.category === category);
};

/**
 * Get stage definition by key
 */
export const getStageDefinition = (
  key: string,
): StageDefinition | undefined => {
  return STAGE_DEFINITIONS[key];
};

/**
 * Helper function to create stage filter handler with search reset
 */
export const createStageFilterHandler = (
  setStageFilter: (stage: string) => void,
  setTypeFilter: (type: string) => void,
  setSearchTerm: (term: string) => void,
) => {
  return (stageKey: string) => {
    setStageFilter(stageKey);
    setTypeFilter("all");
    setSearchTerm(""); // Reset search term when stage is selected
  };
};

/**
 * Helper function to create "All" filter handler with search reset
 */
export const createAllFilterHandler = (
  setStageFilter: (stage: string) => void,
  setTypeFilter: (type: string) => void,
  setSearchTerm: (term: string) => void,
) => {
  return () => {
    setStageFilter("all");
    setTypeFilter("all");
    setSearchTerm(""); // Reset search term when "All" is selected
  };
};
